/* eslint-disable no-unused-vars */

import { useCallback, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { usePermissions } from './use-permissions';
import { drawerItemsV2 } from 'src/layouts/DashboardLayout/constants';
import {
  DrawerItemLabelEnum,
  DrawerItemSection
} from 'src/layouts/DashboardLayout/types';
import { useRoles } from './use-roles';
import { slices, useAppDispatch } from 'src/redux';

const { actions: appActions } = slices.app;

export const useDrawerItemsWithPerms = () => {
  const permissions = usePermissions();
  const { isOwner } = useRoles();
  const dispatch = useAppDispatch();

  const drawerItemsWithPermissions = useMemo(() => {
    const clonedDrawerItems = cloneDeep(drawerItemsV2);
    const canViewCustomerQueueing = permissions.canViewCustomerQueueing;
    const canViewSales =
      permissions.permissionChecker('view_sales') ||
      permissions.permissionChecker('can_view_sales_basic_dashboard');
    const canViewCustomers = permissions.permissionChecker(
      'can_view_customers'
    );
    const canManageUsers = permissions.permissionChecker('manage_users');
    const canViewBanners = permissions.permissionChecker('view_ecomm_banners');
    const canViewAvailableInventory = permissions.canSeeAvailableInventory;
    const canManagePermissions = permissions.permissionChecker(
      'edit_permissions_roles'
    );
    const canViewFlashDeals = permissions.canViewFlashDeals;
    const canViewEcomUserAccount = permissions.canViewEcomUserAccount;
    const canEditBranches = permissions.canEditBranches;
    const canEditOptions = permissions.canEditOptions;
    const canSMSBlast = permissions.canSMSBlast;
    const canViewCodeEvent = permissions.canViewCodeEvent;
    const canViewMinigame = permissions.canViewMiniGame;
    const canViewKachiPoints = permissions.canViewKachiPoints;
    const featureToggleKachiPoints = permissions.featureToggleKachiPoints;
    const viewStockTransfer = permissions?.canViewStockTransfer;
    const viewTransferLogs = permissions?.canViewStockTransferLogs;
    const viewOutOPfStockProducts = permissions?.viewRecentOutOfStockProducts;
    const viewPriceListV2 = permissions?.viewPricelist;
    const viewDecayingStock = permissions?.canViewDecayingStocks;
    const viewFastMovingProducts = permissions?.canViewFastMovingProducts;
    const canViewEcomOrder = permissions?.canViewEcomOrder;
    const viewEcommSavedBuilds = permissions?.canViewEcommSavedBuilds;
    const viewBlog = permissions?.canViewBlog;
    const canViewBlogCategory = permissions?.canViewBlogCategory;
    const canViewTaptap = permissions?.canViewTaptap;
    const canViewTaptapReport = permissions?.canViewTaptapReport;

    const canViewInventoryAudit = permissions?.canInventoryAudit;
    const canViewInventoryAuditLogs = permissions?.canViewAuditLogs;
    const canCreatePromotional = permissions?.canCreatePromotional;
    const canViewProductInPO = permissions?.canViewProductInPO;
    const canViewPoCheque = permissions.canViewPoCheque;

    // RMA
    const canViewRMA = permissions?.canViewRMA;

    // Voucher
    const canReadVoucher = permissions?.canReadVoucher;

    // Purchase Order
    const canViewPurchaseOrder = permissions?.canViewPurchaseOrder;
    const canViewPurchaseOrderReports =
      permissions?.canViewPurchaseOrderReports;

    const isNerfed = permissions?.isNerfed;
    // Sell Out
    const canViewSellOut = permissions?.canViewSellOut;

    // Liability modules
    const canViewLiabilityChart = permissions?.canViewLiabilityChart;
    const canViewLiabilitySchedule = permissions?.canViewLiabilitySchedule;
    const canViewSalesDashboard = permissions?.viewSalesDashboard;
    const canViewManufacturers = permissions?.canViewManufacturers;

    const canViewQuotation = permissions?.canViewQuotation;
    const canViewTransactions = permissions.canViewTransactions;
    const canViewInventory = permissions?.canViewInventory;
    const canViewProducts = permissions?.canViewProducts;
    const canViewCategories = permissions?.canViewCategories;
    const canViewSuppliers = permissions?.canViewSuppliers;
    const canViewGames = permissions?.canViewGames;
    const canViewPCPackage = permissions?.canViewPCPackage;
    const canViewPCGallery = permissions?.canViewPCGallery;
    const canViewEvent = permissions?.canViewEvent;
    const canViewTaxCertificate = permissions?.canViewTaxCertificate;
    const canViewPCBundle = permissions?.canViewPCBundle;
    const canViewEmailer = permissions?.canViewEmailer;
    const canViewCustomerPayment = permissions?.canViewCustomerPayment;
    const canViewCRMSales = permissions?.canViewCrmSales;

    const filteredViaPermissions = clonedDrawerItems?.map((y) => {
      const filteredSectionItems = y?.sectionItems?.filter((x) => {
        if (
          x.title === DrawerItemLabelEnum.Liability_Chart &&
          !canViewLiabilityChart
        ) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Liability_Schedule &&
          !canViewLiabilitySchedule
        ) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Sales && !canViewSales) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Customers && !canViewCustomers) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Customers_Queueing &&
          !canViewCustomerQueueing
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Manage_Users && !canManageUsers) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Roles_Permissions &&
          !canManagePermissions
        ) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Ecomm_Banners && !canViewBanners) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Flash_Deals && !canViewFlashDeals) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Available_Inventory &&
          !canViewAvailableInventory
        ) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Options && !canEditOptions) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.SMS_Blast && !canSMSBlast) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.PcwUser &&
          !canViewEcomUserAccount
        ) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.EventCode && !canViewCodeEvent) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Minigame && !canViewMinigame) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Branches && !canEditBranches) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.KachiPoints &&
          !canViewKachiPoints &&
          !featureToggleKachiPoints
        ) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Stock_Transfer &&
          !viewStockTransfer
        ) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Transfer_Logs &&
          !viewTransferLogs
        ) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Out_Of_Stock &&
          !viewOutOPfStockProducts
        ) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Price_List_V2 && !viewPriceListV2) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Decaying_Stock &&
          !viewDecayingStock
        ) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Fast_Moving_Products &&
          !viewFastMovingProducts
        ) {
          return false;
        }
        if (x.title === DrawerItemLabelEnum.Ecom_Orders && !canViewEcomOrder) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Saved_Builds &&
          !viewEcommSavedBuilds
        ) {
          return false;
        }
        //===blog===
        if (x.title === DrawerItemLabelEnum.Blogs && !viewBlog) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Blog_Category &&
          !canViewBlogCategory
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.TapTap && !canViewTaptap) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.TapTap_Report &&
          !canViewTaptapReport
        ) {
          return false;
        }

        //=== inventory audit ===
        if (
          x.title === DrawerItemLabelEnum.Inventory_Audits &&
          !canViewInventoryAudit
        ) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Inventory_Audits_Logs &&
          !canViewInventoryAuditLogs
        ) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Promotional &&
          !canCreatePromotional
        ) {
          return false;
        }

        // RMA
        if (x.title === DrawerItemLabelEnum.RMA && !canViewRMA) {
          return false;
        }

        // Voucher
        if (x.title === DrawerItemLabelEnum.Vouchers && !canReadVoucher) {
          return false;
        }

        // Sell Out
        if (x.title === DrawerItemLabelEnum.Sell_Out && !canViewSellOut) {
          return false;
        }

        // Purchase Order
        if (
          x.title === DrawerItemLabelEnum.Purchase_Orders &&
          !canViewPurchaseOrder
        ) {
          return false;
        }
        if (
          x.title === DrawerItemLabelEnum.Purchase_Order_Reports &&
          !canViewPurchaseOrderReports
        ) {
          return false;
        }

        // Product in
        if (x.title === DrawerItemLabelEnum.Product_In && !canViewProductInPO) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Cheque_Management &&
          !canViewPoCheque
        ) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Dashboard &&
          !canViewSalesDashboard
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Quotation && !canViewQuotation) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Transactions &&
          !canViewTransactions
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.PC_Bundles && !canViewPCBundle) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.PC_Build_Gallery &&
          !canViewPCGallery
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Games && !canViewGames) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Suppliers && !canViewSuppliers) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Manufacturers &&
          !canViewManufacturers
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Products && !canViewProducts) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Inventory && !canViewInventory) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Categories && !canViewCategories) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.PC_Package && !canViewPCPackage) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.EventSubscriptions &&
          !canViewEvent
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.Emailer && !canViewEmailer) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.Tax_Certification &&
          !canViewTaxCertificate
        ) {
          return false;
        }

        if (
          x.title === DrawerItemLabelEnum.CustomerPayment &&
          !canViewCustomerPayment
        ) {
          return false;
        }

        if (x.title === DrawerItemLabelEnum.CRMSales && !canViewCRMSales) {
          return false;
        }

        // if is_nerfed permission is true, hide all items but only show the following items
        // - quotation, inventory audits, quotation, taptap
        const allowedTitlesOnNerfed = [
          DrawerItemLabelEnum.Quotation
          // DrawerItemLabelEnum.Inventory_Audits,
          // DrawerItemLabelEnum.TapTap
        ];

        if (isNerfed && !allowedTitlesOnNerfed.includes(x.title)) {
          // there are roles that are nerfed but still have access to some items
          // isOwner is a special case, so we need to check if the user is an owner
          // if the user is an owner, we need to show all items
          if (isOwner) {
            return true;
          }

          return false;
        }

        return true;
      });

      const newItem = {
        sectionTitle: y?.sectionTitle,
        sectionItems: filteredSectionItems
      };
      return newItem;
    });

    const filteredIfHasItems = filteredViaPermissions?.filter(
      (x) => x?.sectionItems?.length > 0
    );

    return filteredIfHasItems;
  }, [isOwner, permissions]);

  const [filteredDrawerItems, setFilteredDrawerItems] = useState<
    DrawerItemSection[]
  >([]);
  const [drawerKeyword, setDrawerKeyword] = useState<string>('');

  const onToggleSection = (index: number) => {
    dispatch(appActions.toggleDrawerSection(index));
  };

  const setOpenDrawerSectionIndices = (payload: number[]) => {
    dispatch(appActions.setOpenDrawerSectionIndices(payload));
  };

  const handleSearchDrawerItems = (val: string) => {
    if (!val) {
      setFilteredDrawerItems(drawerItemsWithPermissions);
      setOpenDrawerSectionIndices([]);
      return;
    }

    const query = val.toLowerCase();

    const matchedSections: DrawerItemSection[] = [];
    const openDrawerIndices: number[] = [];

    drawerItemsWithPermissions.forEach((section) => {
      const filteredItems = section.sectionItems.filter((item) =>
        item.title.toLowerCase().includes(query)
      );

      if (filteredItems.length === 0) {
        return;
      }

      // if empty push zero, and then if [0] push one, creating [0,1,2] and so on...
      openDrawerIndices.push(openDrawerIndices.length);
      matchedSections.push({
        ...section,
        sectionItems: filteredItems
      });
    });

    if (matchedSections.length === 0) {
      setFilteredDrawerItems(drawerItemsWithPermissions);
      setOpenDrawerSectionIndices([]);
      return;
    }

    setOpenDrawerSectionIndices(openDrawerIndices);
    setFilteredDrawerItems(matchedSections);
  };

  return {
    drawerItemsWithPermissions,
    filteredDrawerItems,
    drawerKeyword,

    setDrawerKeyword,
    setFilteredDrawerItems,
    handleSearchDrawerItems,
    onToggleSection
  };
};
